import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { AbstractAgent } from '@ag-ui/client';
import { useAgUiSetState } from '@assistant-ui/react-ag-ui';
import type { AgUiAssistantRuntime } from '@assistant-ui/react-ag-ui';
import { v4 as uuidv4 } from 'uuid';

import { selectors as offerReduxSelectors } from '@makemydeal/dr-offer-redux';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import type { DealStructureContext } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { getPrimaryScopeState } from '@makemydeal/dr-dash-store/selectors/primaryScopeSelectors';
import {
    getPrimaryDealStructureContext,
    getPrimaryDealStructureReadiness
} from '@makemydeal/dr-dash-store/selectors/dealStructureContextSelectors';
import type { DealStructureReadiness } from '@makemydeal/dr-dash-store/selectors/dealStructureContextSelectors';

import { trackDealStructureFailed, trackDealStructureSkipped, trackSendMessage } from '../monitoring/dealAssistantMonitoring';
import type { DealAssistantUnavailableReason } from './DealAssistantContext';

const STATE_KEY = 'dealStructure';

type DealAssistantState = StateTree & RealState;

type RuntimeState = Record<string, unknown>;

const isPlainObject = (value: unknown): value is RuntimeState =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const safe = <T>(read: () => T, fallback: T, onError?: (error: unknown) => void): T => {
    try {
        return read();
    } catch (error) {
        onError?.(error);
        return fallback;
    }
};

const selectScopeId = (state: DealAssistantState, onError?: (error: unknown) => void): string | undefined =>
    safe(() => scopedStateSelectors.getPrimaryScopeId(state) || undefined, undefined, onError);

const selectDealXgId = (state: DealAssistantState, onError?: (error: unknown) => void): string | undefined =>
    safe(() => offerReduxSelectors.getDealXgId(getPrimaryScopeState(state)) || undefined, undefined, onError);

const dealChanged = (threadDealXgId: string | undefined, currentDealXgId: string | undefined) =>
    threadDealXgId !== undefined && currentDealXgId !== undefined && threadDealXgId !== currentDealXgId;

const readReadiness = (state: DealAssistantState, onError?: (error: unknown) => void): DealStructureReadiness =>
    selectScopeId(state, onError) === undefined
        ? 'missing-inputs'
        : safe(() => getPrimaryDealStructureReadiness(state), 'missing-inputs', onError);

const readUnavailableReason = (
    state: DealAssistantState,
    onError: (error: unknown) => void
): DealAssistantUnavailableReason | undefined => {
    const readiness = readReadiness(state, onError);
    return readiness === 'ready' ? undefined : readiness;
};

const reportOnce = () => {
    let reported = false;
    return (error: unknown) => {
        if (!reported) {
            reported = true;
            trackDealStructureFailed(error);
        }
    };
};

export type DealStructureCapture = { snapshot: DealStructureContext } | { skipped: DealAssistantUnavailableReason };

const withDealStructure = (prev: unknown, snapshot: DealStructureContext): RuntimeState => ({
    ...(isPlainObject(prev) ? prev : {}),
    [STATE_KEY]: snapshot
});

export type UseDealStructureSnapshotParams = {
    runtime: AgUiAssistantRuntime;
    agent: AbstractAgent;
    clearFailure: () => void;
    getDealStructureSnapshotLabel?: () => string | undefined;
};

export const useDealStructureSnapshot = ({
    runtime,
    agent,
    clearFailure,
    getDealStructureSnapshotLabel
}: UseDealStructureSnapshotParams) => {
    const store = useStore<DealAssistantState>();
    const setRuntimeState = useAgUiSetState<RuntimeState>();
    const currentDealXgId = useSelector(selectDealXgId);

    const [dealStructureSnapshot, setDealStructureSnapshot] = useState<DealStructureContext | undefined>(undefined);
    const [dealStructureSnapshotLabel, setDealStructureSnapshotLabel] = useState<string | undefined>(undefined);
    const hasSnapshot = dealStructureSnapshot !== undefined;
    const [reportRenderRead] = useState(reportOnce);
    const unavailableReason = useSelector((state: DealAssistantState) =>
        hasSnapshot ? undefined : readUnavailableReason(state, reportRenderRead)
    );

    const setRuntimeStateRef = useRef(setRuntimeState);
    setRuntimeStateRef.current = setRuntimeState;

    const getLabelRef = useRef(getDealStructureSnapshotLabel);
    getLabelRef.current = getDealStructureSnapshotLabel;

    const snapshotRef = useRef<DealStructureContext | undefined>(undefined);
    const threadDealXgIdRef = useRef<string | undefined>(undefined);
    const turnReporterRef = useRef<((error: unknown) => void) | undefined>(undefined);

    const turnReporter = useCallback(() => {
        if (!turnReporterRef.current) {
            turnReporterRef.current = reportOnce();
            void Promise.resolve().then(() => {
                turnReporterRef.current = undefined;
            });
        }
        return turnReporterRef.current;
    }, []);

    const bindThread = useCallback((current: string | undefined) => {
        threadDealXgIdRef.current = threadDealXgIdRef.current ?? current;
    }, []);

    const resetThread = useCallback(() => {
        if (runtime.thread.getState().isRunning) {
            runtime.thread.cancelRun();
        }
        runtime.thread.reset();
        setRuntimeStateRef.current({});
        agent.threadId = uuidv4();
        snapshotRef.current = undefined;
        threadDealXgIdRef.current = undefined;
        setDealStructureSnapshot(undefined);
        setDealStructureSnapshotLabel(undefined);
        clearFailure();
    }, [runtime, agent, clearFailure]);

    const capture = useCallback(
        (onError: (error: unknown) => void = turnReporter()): DealStructureCapture => {
            const kept = snapshotRef.current;
            if (kept !== undefined && runtime.thread.getState().messages.length > 0) {
                return { snapshot: kept };
            }
            const state = store.getState();
            const readiness = readReadiness(state, onError);
            const context =
                readiness === 'ready' ? safe(() => getPrimaryDealStructureContext(state), undefined, onError) : undefined;
            if (!context) {
                if (kept !== undefined) {
                    return { snapshot: kept };
                }
                return { skipped: readiness === 'ready' ? 'no-context' : readiness };
            }
            const label = safe(() => getLabelRef.current?.() || undefined, undefined, onError);
            snapshotRef.current = context;
            setDealStructureSnapshot(context);
            setDealStructureSnapshotLabel(label);
            bindThread(selectDealXgId(state, onError));
            return { snapshot: context };
        },
        [store, runtime, bindThread, turnReporter]
    );

    const send = useCallback(
        async (text: string) => {
            const onError = turnReporter();
            const current = selectDealXgId(store.getState(), onError);
            if (dealChanged(threadDealXgIdRef.current, current)) {
                resetThread();
            }
            const captured = capture(onError);
            if ('skipped' in captured) {
                trackDealStructureSkipped(captured.skipped);
                return;
            }
            clearFailure();
            bindThread(current);

            const { snapshot } = captured;
            setRuntimeStateRef.current((prev) => withDealStructure(prev, snapshot));

            trackSendMessage();
            await runtime.thread.append(text);
        },
        [store, runtime, capture, bindThread, resetThread, clearFailure, turnReporter]
    );

    useEffect(() => {
        if (dealChanged(threadDealXgIdRef.current, currentDealXgId)) {
            resetThread();
        } else if (snapshotRef.current !== undefined || runtime.thread.getState().messages.length > 0) {
            bindThread(currentDealXgId);
        }
    }, [currentDealXgId, runtime, bindThread, resetThread]);

    return {
        isAvailable: unavailableReason === undefined,
        unavailableReason,
        dealStructureSnapshot,
        dealStructureSnapshotLabel,
        capture,
        send
    };
};
